// Portal del proveedor con sesion: trae las habilitaciones de la cuenta y
// arma el shell (src/shell) con las secciones de portal/sections.tsx. Ninguna
// planta es "la" planta: el filtro global la elige y la URL la recuerda.
//
// La encuesta de ofertas (PP-71) es una sola y vive aca, arriba del shell:
// la leen el aviso de oferta nueva (alrededor de la seccion activa, el
// `frame`), el Inicio, y la cabecera y la barra lateral (reloj de la planta,
// "Ofertas al día" y contador de Viajes, header-status/). Pregunta solo
// mientras la cuenta tiene plantas. El aviso suena y marca el titulo este
// abierta la seccion que este; su silencio lo recuerda el navegador y se
// cambia en el menu de cuenta.
//
// Copy impersonal, que es el registro de las vistas administrativas
// (gestion/07_identidad/readme.md).
import { useCallback, useMemo, type ReactNode } from "react";
import type { PortalSession } from "../auth/session.js";
import type { FleetPort } from "../fleet/port.js";
import type { MultiSitePort } from "../multi-site/port.js";
import { ToastProvider } from "../components/ui/toast.js";
import { OfferAlert, OffersFeedProvider, pageChime, useMuted, type Chime } from "../offer-alert/index.js";
import type { NotificationsPort } from "../notifications/port.js";
import { useOfferMail } from "../notifications/useOfferMail.js";
import { systemClock, type Clock } from "../requests/clock.js";
import type { RequestsPort } from "../requests/port.js";
import type { SaveFile, SettlementPort, TripExportPort } from "../settlements/index.js";
import { HeaderStatusBar, TripsCounter } from "../header-status/HeaderStatus.js";
import { PortalShell } from "../shell/PortalShell.js";
import type { SectionContext, ShellScope } from "../shell/types.js";
import type { SitesPort } from "../sites/port.js";
import { usePlantas } from "../sites/useSites.js";
import { portalSections, SECTION_PATHS } from "./sections.js";

interface Props {
  session: PortalSession;
  port: SitesPort;
  fleetPort: FleetPort;
  requestsPort: RequestsPort;
  multiSitePort: MultiSitePort;
  /** Avisos de la cuenta (PP-68). Sin puerto, el menu de cuenta no muestra el aviso. */
  notificationsPort?: NotificationsPort;
  onLogout: () => void;
  /** Reloj de la bandeja, del Inicio y del aviso; los tests lo reemplazan. */
  clock?: Clock;
  /** Aviso sonoro de oferta nueva; los tests lo reemplazan. Por defecto, Web Audio. */
  chime?: Chime;
  /** Exportable de viajes sin montos (LQ-1). */
  tripExportPort: TripExportPort;
  /** Liquidacion con montos (LQ-4). */
  settlementPort: SettlementPort;
  /** Entrega del archivo descargado; los tests la reemplazan. */
  saveFile?: SaveFile;
}

// Puerto que nunca contesta: el hook no se llama condicionalmente y, sin
// puerto real, el aviso no se pinta (ver el spread de abajo).
const SIN_AVISOS: NotificationsPort = {
  read: () => new Promise(() => undefined),
  save: () => new Promise(() => undefined),
};

export function Portal({ session, port, fleetPort, requestsPort, multiSitePort, notificationsPort, onLogout, clock, chime, tripExportPort, settlementPort, saveFile }: Props): React.JSX.Element {
  const plantas = usePlantas(port, session);
  const { aviso, cambiar } = useOfferMail(notificationsPort ?? SIN_AVISOS, session);
  const [muted, setMuted] = useMuted();
  const bell = useMemo(() => chime ?? pageChime(), [chime]);
  const alertClock = clock ?? systemClock;
  const frame = useCallback(
    (context: SectionContext, section: ReactNode) => (
      <>
        <OfferAlert sites={context.sites} siteId={context.siteId} muted={muted} chime={bell} clock={alertClock} />
        {section}
      </>
    ),
    [alertClock, muted, bell],
  );
  const headerStatus = useCallback((scope: ShellScope) => <HeaderStatusBar scope={scope} clock={alertClock} />, [alertClock]);
  // Solo Viajes lleva contador: es donde se responde y se asigna.
  const navBadges = useCallback(
    (path: string, scope: ShellScope) => (path === SECTION_PATHS.trips ? <TripsCounter scope={scope} clock={alertClock} /> : null),
    [alertClock],
  );
  const hasSites = plantas.estado === "listo" && plantas.plantas.length > 0;
  const onCambiarSonido = useCallback((encendido: boolean) => setMuted(!encendido), [setMuted]);
  const sections = useMemo(
    () =>
      portalSections({
        fleetPort,
        requestsPort,
        multiSitePort,
        tripExportPort,
        settlementPort,
        ...(clock === undefined ? {} : { clock }),
        ...(saveFile === undefined ? {} : { saveFile }),
      }),
    [fleetPort, requestsPort, multiSitePort, tripExportPort, settlementPort, clock, saveFile],
  );
  // Los toasts de las secciones (rediseño, decision 5) viven arriba del shell:
  // sobreviven al cambio de seccion y se apilan abajo a la derecha.
  return (
    <ToastProvider>
      <OffersFeedProvider port={multiSitePort} session={session} clock={alertClock} enabled={hasSites}>
        <PortalShell
          session={session}
          plantas={plantas}
          sections={sections}
          onLogout={onLogout}
          frame={frame}
          headerStatus={headerStatus}
          navBadges={navBadges}
          sonido={!muted}
          onCambiarSonido={onCambiarSonido}
          {...(notificationsPort === undefined ? {} : { aviso, onCambiarAviso: cambiar })}
        />
      </OffersFeedProvider>
    </ToastProvider>
  );
}
